import { useCallback, useEffect, useState } from 'react';
import type { AutomationWorkflow } from '../types/AutomationWorkflow.types';
import {
  deleteAutomation,
  fetchAutomations,
  runAutomation,
  saveAutomationWorkflow,
  subscribeToAutomations,
} from '../services/Automations.service';
import type {
  AutomationGraph,
  AutomationTrigger,
} from '../types/AutomationWorkflow.types';
import type { AgentModelSelection } from '../components/agentChat/AgentChat.types';

export const useAutomationWorkflows = () => {
  const [automations, setAutomations] = useState<AutomationWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const saved = await fetchAutomations();
        if (!cancelled) setAutomations(saved);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    const unsubscribe = subscribeToAutomations((updated) => {
      if (!cancelled) setAutomations(updated);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const save = useCallback(
    async (input: {
      id?: number | null;
      name: string;
      trigger: AutomationTrigger;
      graph: AutomationGraph;
      enabled: boolean;
      model?: AgentModelSelection | null;
    }): Promise<AutomationWorkflow | null> => {
      const result = await saveAutomationWorkflow(input);
      if (!result.saved || !result.automation) {
        setError(result.error ?? 'Could not save that automation.');
        return null;
      }
      setError(null);
      setAutomations(await fetchAutomations());
      return result.automation;
    },
    [],
  );

  const remove = useCallback(async (id: number) => {
    setAutomations(await deleteAutomation(id));
  }, []);

  const start = useCallback(async (id: number): Promise<string | null> => {
    const result = await runAutomation(id);
    if (!result.started) {
      setError(result.error ?? 'Could not run that automation.');
      return null;
    }
    setError(null);
    return result.runId ?? null;
  }, []);

  return { automations, loading, error, save, remove, start };
};

export default useAutomationWorkflows;
